import { Link } from '@tanstack/react-router';
import { FileUp, RotateCcw } from 'lucide-react';
import { AppChrome } from '@/components/AppChrome';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress, ProgressValue } from '@/components/ui/progress';
import { SURFACES } from '@/lib/gql/__generated__/surfaces';
import { useSurfaceAccess } from '@/lib/platform/surfaces';
import { useFileUpload } from '@/lib/platform/uploads';
import { useFiling } from './state';

export function IntakePage() {
  const access = useSurfaceAccess(SURFACES['source-intake']);
  const filingId = useFiling((snapshot) => snapshot.context.filingId);
  const upload = useFileUpload();
  if (access === 'waiting') return <p className="p-8 text-sm text-muted-foreground">Checking screen access…</p>;
  if (access === 'unavailable')
    return <p className="p-8 text-sm text-destructive">This screen is unavailable right now.</p>;
  if (access === 'denied')
    return <p className="p-8 text-sm text-muted-foreground">You have no access to this screen.</p>;

  return (
    <AppChrome title="Source intake" description="Hand in the consolidated brokerage statement for the open Filing.">
      {!filingId ? (
        <Card>
          <CardContent className="space-y-3 p-6">
            <p className="font-medium">No Filing is open.</p>
            <p className="text-sm text-muted-foreground">Open a Filing before handing in a statement.</p>
            <Button render={<Link to="/filing-setup" />}>Go to Filing setup</Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <FileUp className="size-4 text-primary" />
              Statement hand-in
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <p className="text-sm text-muted-foreground">
              Filing selected. The file will be stored by TrustFile before any filing action can use it.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <label
                htmlFor="statement-file"
                className="inline-flex min-h-11 cursor-pointer items-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus-within:outline-2 focus-within:outline-ring"
              >
                Choose brokerage statement
              </label>
              <input
                id="statement-file"
                type="file"
                accept=".pdf,.csv,.xlsx"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void upload.pick(file);
                }}
              />
              {upload.status === 'stored' && <span className="text-sm text-primary">Stored and ready.</span>}
              {(upload.status === 'uploading' || upload.status === 'settling') && (
                <span className="text-sm text-muted-foreground">
                  {upload.status === 'uploading' ? 'Uploading…' : 'Confirming storage…'}
                </span>
              )}
              {upload.status === 'rejected' && (
                <span className="text-sm text-destructive">The file could not be stored. Try again.</span>
              )}
            </div>
            {upload.progress !== null && (
              <Progress value={upload.progress * 100} aria-label="Upload progress">
                <ProgressValue />
              </Progress>
            )}
            {upload.error && (
              <p className="text-sm text-destructive">The statement could not be stored. Try another file.</p>
            )}
            {upload.status !== 'idle' && (
              <Button variant="ghost" size="sm" onClick={upload.reset}>
                <RotateCcw className="mr-2 size-4" />
                Choose another file
              </Button>
            )}
            {upload.status === 'stored' && (
              <p className="border-l-2 border-accent px-3 text-sm text-muted-foreground">
                The Filing hand-in command is not connected to a server field in this workspace, so no claim is made
                that the statement was applied.
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </AppChrome>
  );
}
